import { useEffect } from 'react';

/**
 * SEO Head Component - Updates document head with meta tags
 * Works with React 19 without external dependencies
 */
export default function SEOHead({
  title,
  description,
  keywords,
  canonical,
  ogTitle,
  ogDescription,
  ogImage,
  ogUrl,
  ogType = 'website',
  twitterCard = 'summary_large_image',
  twitterTitle,
  twitterDescription,
  twitterImage,
  robots = 'index, follow',
  author = 'Visibeen',
  geoRegion,
  geoPlacename,
  geoPosition,
  extraMeta = {}
}) {
  useEffect(() => {
    // Update title
    if (title) {
      document.title = title;
    }

    // Helper function to set or update meta tag
    const setMetaTag = (name, content, isProperty = false) => {
      if (!content) return;

      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);

      if (element) {
        element.setAttribute('content', content);
      } else {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        element.setAttribute('content', content);
        document.head.appendChild(element);
      }
    };

    // Helper function to set or update link tag
    const setLinkTag = (rel, href) => {
      if (!href) return;

      let element = document.querySelector(`link[rel="${rel}"]`);

      if (element) {
        element.setAttribute('href', href);
      } else {
        element = document.createElement('link');
        element.setAttribute('rel', rel);
        element.setAttribute('href', href);
        document.head.appendChild(element);
      }
    };

    // Basic meta tags
    setMetaTag('description', description);
    setMetaTag('keywords', keywords);
    setMetaTag('robots', robots);
    setMetaTag('author', author);

    // Canonical URL
    setLinkTag('canonical', canonical);

    // Open Graph tags
    setMetaTag('og:title', ogTitle || title, true);
    setMetaTag('og:description', ogDescription || description, true);
    setMetaTag('og:image', ogImage, true);
    setMetaTag('og:url', ogUrl || canonical, true);
    setMetaTag('og:type', ogType, true);
    setMetaTag('og:site_name', 'Visibeen Directory', true);
    setMetaTag('og:locale', 'en_IN', true);

    // Twitter Card tags
    setMetaTag('twitter:card', twitterCard);
    setMetaTag('twitter:title', twitterTitle || ogTitle || title);
    setMetaTag('twitter:description', twitterDescription || ogDescription || description);
    setMetaTag('twitter:image', twitterImage || ogImage);
    setMetaTag('twitter:site', '@visibeen');
    setMetaTag('twitter:creator', '@visibeen');

    // Geo tags
    if (geoRegion) setMetaTag('geo.region', geoRegion);
    if (geoPlacename) setMetaTag('geo.placename', geoPlacename);
    if (geoPosition) setMetaTag('geo.position', geoPosition);
    if (geoPosition) setMetaTag('ICBM', geoPosition.replace(';', ','));

    // Extra meta tags
    Object.entries(extraMeta).forEach(([key, value]) => {
      const isProperty = key.startsWith('og:') || key.startsWith('business:');
      setMetaTag(key, value, isProperty);
    });

    // Cleanup function (optional - restore original title on unmount)
    return () => {
      // You can restore default meta tags here if needed
    };
  }, [
    title,
    description,
    keywords,
    canonical,
    ogTitle,
    ogDescription,
    ogImage,
    ogUrl,
    ogType,
    twitterCard,
    twitterTitle,
    twitterDescription,
    twitterImage,
    robots,
    author,
    geoRegion,
    geoPlacename,
    geoPosition,
    extraMeta
  ]);

  return null; // This component doesn't render anything
}

/**
 * Helper component for setting link tags (prev/next for pagination)
 */
export function SEOLinks({ prev, next }) {
  useEffect(() => {
    const setLinkTag = (rel, href) => {
      if (!href) return;

      // Remove existing
      const existing = document.querySelector(`link[rel="${rel}"]`);
      if (existing) existing.remove();

      // Add new
      const element = document.createElement('link');
      element.setAttribute('rel', rel);
      element.setAttribute('href', href);
      document.head.appendChild(element);
    };

    setLinkTag('prev', prev);
    setLinkTag('next', next);

    return () => {
      // Cleanup
      const prevLink = document.querySelector('link[rel="prev"]');
      const nextLink = document.querySelector('link[rel="next"]');
      if (prevLink) prevLink.remove();
      if (nextLink) nextLink.remove();
    };
  }, [prev, next]);

  return null;
}
